import { LitElement, PropertyValues, css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ResizeController } from '@lit-labs/observers/resize-controller.js';
import { LitHauntedElement } from '../mixins/lit-haunted-element';
import { useEffect } from 'haunted';

@customElement('ep-segmented-picker-option')
export class EPSegmentedPickerOption extends LitElement {

  @property({ type: Boolean, reflect: true })
  selected: boolean = false;

  @property({ type: String, reflect: true })
  value: string = '';

  @property({ reflect: false })
  forceHideSeparator: boolean = false

  @query('slot')
  slotElement?: HTMLSlotElement;
  @query('[data-text]')
  slotWrapperElement?: HTMLButtonElement;

  get index(): number {
    if (this.parentNode === null) return -1;
    let i = 0;
    for(let item of this.siblings) {
      if (item === this) return i;
      i++;
    }
    return -1;
  }

  get siblings(): EPSegmentedPickerOption[] {
    if (this.parentNode === null) return [];
    return Array.from(this.parentNode.children).filter(e => e instanceof EPSegmentedPickerOption) as any;
  }

  private _handleClick() {
    const event = new CustomEvent('ep-picker-select', { 
      bubbles: true,
      composed: true,
    })
    this.dispatchEvent(event);
  }

  render() {
    return html`
      <li data-selected=${this.selected} 
        data-is-last-child=${this.index === this.siblings.length - 1}
        data-is-first-child=${this.index === 0}
        data-force-hide-separator=${this.forceHideSeparator}
        >
        <button @click=${this._handleClick}>
          <span class="content">
            <div class="text">
              <slot>Text</slot>
            </div>
          </span>
        </button>
      </li>
    `
  }

  static styles = css`
    /* CSS pulled from Primer (https://primer.style/react/SegmentedControl) */
    :host {
      display: contents;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
      line-height: 1.5;
      color: rgb(36, 41, 47);
    }

    * {
      box-sizing: border-box;
    }

    li {
      display: block;
      position: relative;
      -webkit-box-flex: 1;
      flex-grow: 1;
      margin-top: -1px;
      margin-bottom: -1px;
      --separator-color: #d0d7de;
    }

    li[data-selected=true] {
      --separator-color: transparent;
    }

    li[data-force-hide-separator=true] {
      --separator-color: transparent;
    }

    li[data-selected=true][data-is-first-child=true] {
      margin-left: -1px;
    }

    li:not([data-is-last-child=true]) {
      margin-right: 1px;
    }

    li:not([data-is-last-child=true])::after {
      background-color: var(--separator-color);
      content: "";
      position: absolute;
      right: -2px;
      top: 8px;
      bottom: 8px;
      width: 1px;
      transition: background-color var(--animation-duration) ease;
    }

    button {
      --segmented-control-button-inner-padding: 12px;
      --segmented-control-button-bg-inset: 4px;
      --segmented-control-outer-radius: 6px;
      background-color: transparent;
      border-color: transparent;
      border-radius: var(--segmented-control-outer-radius);
      border-width: 0px;
      color: currentcolor;
      cursor: pointer;
      font-family: inherit;
      font-size: inherit;
      font-weight: 400;
      padding: var(--segmented-control-button-bg-inset);
      height: 100%;
      width: 100%;
      transition: transform 125ms ease 0ms;
    }

    li[data-selected=true] button {
      font-weight: 600;
      /* Removed for animated approach */
      /* padding: 0px; */
    }

    button:focus:not(:focus-visible) {
      outline: none;
      box-shadow: none;
    }

    /* li[data-selected=false] button:active {
      transform: scale(0.95);
    } */

    span.content {
      -webkit-box-align: center;
      align-items: center;
      background-color: transparent;
      border-color: transparent;
      border-style: solid;
      border-width: 1px;
      border-radius: calc(var(--segmented-control-outer-radius) - var(--segmented-control-button-bg-inset) / 2);
      display: flex;
      height: 100%;
      -webkit-box-pack: center;
      justify-content: center;
      padding-left: calc(var(--segmented-control-button-inner-padding) - var(--segmented-control-button-bg-inset));
      padding-right: calc(var(--segmented-control-button-inner-padding) - var(--segmented-control-button-bg-inset));
    }

    li:not([data-selected=true]) span.content:hover {
      background-color: rgba(175, 184, 193, 0.2);
    }
    li:not([data-selected=true]) span.content:active {
      background-color: rgba(175, 184, 193, 0.3);
    }

    /* Removed for animated approach */
    /* li[data-selected=true] span.content {
      background-color: rgb(255, 255, 255);
      border-color: rgb(140, 149, 159);
      border-style: solid;
      border-width: 1px;
      border-radius: var(--segmented-control-outer-radius);
      padding-left: var(--segmented-control-button-inner-padding);
      padding-right: var(--segmented-control-button-inner-padding);
    } */

    div.text, ::slotted(*) {
      position: relative;
      /* used to be 2 */
      z-index: 0;
    }

    ::slotted(*) {
    }
  `
}

/**
 * A segmented picker element element
 */
@customElement('ep-segmented-picker')
export class EPSegmentedPicker extends LitHauntedElement {
  @property({ type: Number, attribute: 'selected-index', reflect: true })
  selectedIndex: number = 0;

  @state()
  hasUpdatedAgain = false

  @property({ type: Number, attribute: 'selector-scale' })
  selectorScale: number = 1.1;

  get options(): EPSegmentedPickerOption[] {
    return (this.slotElement?.assignedElements()
      .filter(e => e instanceof EPSegmentedPickerOption) ?? []) as EPSegmentedPickerOption[];
  }

  get selectedOption(): EPSegmentedPickerOption | undefined {
    return this.options[this.selectedIndex];
  }

  get selectedValue(): string | undefined {
    return this.selectedOption?.value;
  }

  @query('slot')
  slotElement?: HTMLSlotElement;

  @query('span.selection')
  selectionElement?: HTMLSpanElement;

  render() {
    useEffect(() => {
      this._dispatchChangeEvent();
      setTimeout(() => this._updateSlotted(), 50);
      if (!this.hasUpdatedAgain) this.hasUpdatedAgain = true;
    }, [this.selectedIndex])

    return html`
      <ul part="root">
        <span class="selection"></span>
        <slot @slotchange=${this._updateSlotted} @ep-picker-select=${this._handlePickerSelect}></slot>
      </ul>
    `
  }

  private _handlePickerSelect(event: CustomEvent) {
    event.stopPropagation()
    if (event.target instanceof EPSegmentedPickerOption) {
      this.selectedIndex = event.target.index;
      this._updateSlotted()
      this._dispatchInputEvent();
    }
  }

  // #region Update "selection bubble"
  private _updateSelectionBubble() {
    const selectedOption = this.selectedOption;
    const pickerBoundingBox = this.shadowRoot?.querySelector('ul')?.getBoundingClientRect();
    const optionBoundingBox = selectedOption?.shadowRoot?.firstElementChild?.getBoundingClientRect();
    //console.log('pickerBoundingBox / optionBoundingBox', pickerBoundingBox, selectedOption?.shadowRoot?.firstElementChild, optionBoundingBox);
    
    if (this.selectionElement && pickerBoundingBox && optionBoundingBox) {
      const optionWidth = optionBoundingBox.width;
      // we don't want to make the width intentionally small
      if (optionWidth === 0) return;
      const optionOffsetFromParent = this.selectedIndex === 0 ? 0 : Math.abs(pickerBoundingBox.x - optionBoundingBox.x);
      //console.log('pickerBB: ', pickerBoundingBox.x, 'optionBB: ', optionBoundingBox.x, 'offset: ', optionOffsetFromParent)
      this.selectionElement.style.width = `${Math.round(optionWidth)}px`;
      this.selectionElement.style.transform = `translateX(${Math.round(optionOffsetFromParent)}px) scale(${this.selectorScale})`

      // hide previous option separators because it makes things look cleaner
      this.options.forEach((option, index) => {
        option.forceHideSeparator = (index === this.selectedIndex || index === (this.selectedIndex - 1));
      });
    }
  }

  protected firstUpdated(_changedProperties: PropertyValues<this>){
    super.firstUpdated(_changedProperties)
    this._updateSelectionBubble();
    setTimeout(() => this._updateSelectionBubble(), 50);
    // I don't think we really need a reference to this
    new ResizeController(this, { 
      target: this,
      // Important so whenever this element is rendered first within something that is "display: none;", it will correct its rendering later
      callback: () => this._updateSelectionBubble(),
      config: { }
    })
  }
  // #endregion

  // #region Keep "selectedIndex" property responsive and dispatch Events

  private _updateSlotted() {
    this.slotElement?.assignedElements().forEach((element, index, array) => {
      if (element instanceof EPSegmentedPickerOption) {
        if (index === this.selectedIndex) {
          element.setAttribute('selected', '');
        }
        else {
          element.removeAttribute('selected');
        }
        //element.selected = index === this.selectedIndex;
      }
    });
  }

  private _dispatchChangeEvent() {
    if (this.hasUpdated && this.hasUpdatedAgain) {
      // https://www.w3.org/TR/DOM-Level-2-Events/events.html#Events-eventgroupings-htmlevents-h3
      this.dispatchEvent(new Event('change', { bubbles: true, cancelable: false }))
      // only update selection bubble when selection changes
      this._updateSelectionBubble();
    }
  }

  private _dispatchInputEvent() {
    // https://www.w3.org/TR/DOM-Level-2-Events/events.html#Events-eventgroupings-htmlevents-h3
    this.dispatchEvent(new Event('input', { bubbles: true, cancelable: false }))
  }

  // #endregion

  static styles = css`
    :host {
      display: contents;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
      line-height: 1.5;
      color: rgb(36, 41, 47);
      --row-height: 32px;
      --animation-duration: 150ms;
    }

    * {
      box-sizing: border-box;
    }

    ul {
      background-color: rgb(234, 238, 242);
      border-radius: 6px;
      display: inline-flex;
      font-size: 14px;
      height: var(--row-height);
      margin: 0px;
      padding: 0px;
      /* important so the child with "position: absolute;" will scroll correctly */
      position: relative;
    }

    span.selection {
      --segmented-control-button-inner-padding: 12px;
      --segmented-control-outer-radius: 6px;
      height: var(--row-height);
      position: absolute;
      /* used to be 1 */
      z-index: 0;
      background-color: rgb(255, 255, 255);
      border-color: rgb(140, 149, 159);
      border-style: solid;
      border-width: 1px;
      border-radius: var(--segmented-control-outer-radius);
      padding-left: var(--segmented-control-button-inner-padding);
      padding-right: var(--segmented-control-button-inner-padding);
      transform: translateX(0px) scaleY(1.0);
      transition: transform var(--animation-duration) ease, width var(--animation-duration) ease;
      will-change: transform;
    }

    ::slotted(ep-segmented-picker-option) {
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-segmented-picker': EPSegmentedPicker,
    'ep-segmented-picker-option': EPSegmentedPickerOption,
  }
}
